import { Alert, Autocomplete, Button, Link, OutlinedInput, Stack, Typography } from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  CountAssetsInContent,
  FlattenObjectListFromGraph,
  GetMetadataByTagName,
} from "../../../Core/Utils/asset-object-utils";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { useSelector } from "react-redux";
import { GetObject } from "../../../Redux/Reducers/redux-slice-asset";
import { assetService } from "../../../Core/Api/services";
import { DuplicateMarkerFromMarkerIdRequest } from "../../../Core/Api/Types/duplicate-marker-from-marker-id-request";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import InventoryIcon from "@mui/icons-material/Inventory";
import { ContentFilterObject } from "../../../Pages/admin/Inventory/InventoryTitleSingle/TitleSingleAssets/title-single-assets";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
  confirm: boolean;
};

export type ModalDuplicateMarkerProps = {
  onClose?: (props: CloseClbProps) => void;
  onConfirm?: () => void;
  markerid: number;
};

export type ModalAddSeasonToBasketProps = CommonModalProps<ModalDuplicateMarkerProps>;

const ModalDuplicateMarker = React.forwardRef<any, ModalAddSeasonToBasketProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onClose, onConfirm, ...modalProps } = props;
  const reference = React.useRef(undefined);
  const enqueueSnackbar = useCustomSnackbar();
  const titleId = useSelector(GetObject)?.parentTitle!.id;
  const AssetContentId = useSelector(GetObject)?.parentObjectId!;
  const [title, setTitle] = React.useState<DbAssetObjectInfos>();
  const [contents, setContents] = React.useState<ContentFilterObject[]>([]);
  const [contentId, setContentId] = React.useState<number>(AssetContentId);
  const [seasonEpisodes, setSeasonEpisodes] = React.useState<number[]>([]);
  const [seasonEpisodesOptions, setSeasonEpisodesOptions] = React.useState<number[]>([]);
  const isTitleSeason = GetMetadataByTagName(title, "titleType")?.value === "Season";

  React.useEffect(() => {
    assetService.GetGraph(titleId!).then((res) => {
      if (res.graph) {
        setTitle(res.graph);
        let contents: ContentFilterObject[] =
          FlattenObjectListFromGraph(res.graph, [ObjectLayer.Content])
            ?.map((content) => {
              // Extract the episode numbers
              const episodes =
                content.childObjects?.reduce((acc: number[], asset) => {
                  const epNumb: number = GetMetadataByTagName(asset, "serieEpisodeNumberProduction")?.value;
                  if (epNumb) {
                    acc.push(epNumb);
                  }
                  return acc;
                }, []) || [];

              return {
                key: content.id,
                contentName: content.name || "",
                childNumber: CountAssetsInContent(content) || 0,
                isDeleted: content.isDeleted || false,
                episodes: episodes.sort((a, b) => a - b),
              };
            })
            .sort() || [];

        setContents(contents);
      }
    });
  }, []);

  React.useEffect(() => {
    setSeasonEpisodes(contents.find((x) => x.key === contentId)?.episodes || []);
    setSeasonEpisodesOptions(contents.find((x) => x.key === contentId)?.episodes || []);
  }, [contentId, contents]);

  // Select/Unselect All Simple
  const handleSelectAllClick = () => {
    handleEditEpisodes(seasonEpisodesOptions);
  };

  const handleUnselectAllClick = () => {
    handleEditEpisodes([]);
  };

  const handleCancelClick = () => {
    onClose && onClose({ confirm: false });
    closeModal();
  };

  const handleConfirmClick = () => {
    onConfirm && onConfirm();

    assetService
      .DuplicateMarkerFromMarkerId(
        new DuplicateMarkerFromMarkerIdRequest({
          markerId: props.markerid,
          contentId: contentId,
          episodeNumbers: seasonEpisodes,
        })
      )
      .then((response) => {
        if (response.success) {
          enqueueSnackbar(t("alert:success.markerDuplicated"), "success");
          onClose && onClose({ confirm: true });
          closeModal();
        }
      });
  };

  //this for simple
  const handleEditEpisodes = (value) => {
    setSeasonEpisodes(value);
  };

  return (
    <Modal
      {...modalProps}
      title={t("modal:title.duplicateMarker")}
      reference={reference}
      closeModal={handleCancelClick}
      width={800}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button
            fullWidth
            disabled={seasonEpisodes.length < 1 || !isTitleSeason}
            variant="contained"
            color="primary"
            onClick={handleConfirmClick}
          >
            {isTitleSeason ? t("modal:title.duplicateMarker") : t("button:availableForSeasonOnly")}
          </Button>
        </Stack>
      }
    >
      {title && (
        <>
          <Stack spacing={2}>
            <Stack>
              <Typography mb={1} component="span" color="text.primary">
                {t("modal:modalDuplicateMarker.body.pleaseChooseTheContent")}
              </Typography>
              <Autocomplete
                popupIcon={<InventoryIcon />}
                options={contents.sort(function (a, b) {
                  if (a.contentName.isEmpty()) return 1;
                  return a.contentName.localeCompare(b.contentName);
                })}
                getOptionLabel={(option) => `${option.contentName} (${option.childNumber} items) `}
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.key} style={option.isDeleted ? { color: "grey" } : { color: "unset" }}>
                      {t("component:body.nameWithNumberItems", { name: option.contentName, count: option.childNumber })}
                    </li>
                  );
                }}
                value={contents.find((x) => x.key === contentId)}
                onChange={(e, value) => setContentId(value!.key)}
                renderInput={(params) => (
                  <OutlinedInput {...params.InputProps} inputProps={params.inputProps} sx={{ width: "100%" }} />
                )}
              />
            </Stack>
            <>
              <Stack>
                <Typography mb={1} component="span" color="text.primary">
                  {t("modal:modalDuplicateMarker.body.pleaseSelectTheEpisode")}
                </Typography>
                <Autocomplete
                  fullWidth
                  options={seasonEpisodesOptions}
                  value={seasonEpisodes}
                  onChange={(e, value) => handleEditEpisodes(value)}
                  popupIcon={<ExpandMoreRoundedIcon />}
                  multiple
                  getOptionLabel={(option) => String(option)}
                  renderInput={(params) => (
                    <OutlinedInput
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      required
                      sx={{ width: "100%" }}
                    />
                  )}
                />
                <Stack mt={2} direction="row" justifyContent="flex-end" spacing={1}>
                  <Link underline="hover" variant="body2" color="primary.text" onClick={handleSelectAllClick}>
                    {t("common:selectAllUpperCase")}
                  </Link>
                  <Link underline="hover" variant="body2" color="primary.text" onClick={handleUnselectAllClick}>
                    {t("common:unselectAllUpperCase")}
                  </Link>
                </Stack>
              </Stack>
            </>
            {AssetContentId !== contentId && (
              <Alert severity="warning">{t("alert:warning.selectedContentIsDifferentFromAssetConent")}</Alert>
            )}
          </Stack>
        </>
      )}
    </Modal>
  );
});

export default ModalDuplicateMarker;
